/**
 * API Client
 * Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    resolve: (value: unknown) => void;
    reject: (reason?: unknown) => void;
    config: InternalAxiosRequestConfig;
  }> = [];

  /**
   * Endpoints where a 401 is the answer, not an expired session.
   * Trying to "refresh" out of a failed login recurses into the refresh queue
   * and leaves the caller's promise permanently unsettled.
   */
  private static readonly AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout', '/auth/mfa'];

  private isAuthPath(url?: string): boolean {
    if (!url) return false;
    return ApiClient.AUTH_PATHS.some((path) => url.includes(path));
  }

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 30000,
      withCredentials: true, // Important: Send HTTP-only cookies
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.client.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Add correlation ID for request tracing
        config.headers['X-Request-ID'] = this.generateRequestId();

        // Add security headers
        config.headers['X-Client-Version'] = import.meta.env.VITE_APP_VERSION || '1.0.0';

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;

        // Handle 401 Unauthorized - attempt token refresh.
        // Credential endpoints are excluded: a 401 from /auth/login means the
        // credentials were wrong, and it must reach the caller as an error.
        if (
          error.response?.status === 401 &&
          !originalRequest?._retry &&
          !this.isAuthPath(originalRequest?.url)
        ) {
          if (this.isRefreshing) {
            // Wait for the in-flight refresh to finish, then replay this request
            return new Promise((resolve, reject) => {
              this.refreshSubscribers.push({ resolve, reject, config: originalRequest });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Attempt to refresh token
            await this.client.post('/auth/refresh');

            // Replay everything that queued up behind the refresh
            const queued = this.refreshSubscribers;
            this.refreshSubscribers = [];
            queued.forEach(({ resolve, reject, config }) => {
              this.client(config).then(resolve, reject);
            });

            // Retry original request
            return this.client(originalRequest);
          } catch (refreshError) {
            // Refresh failed - fail the queue too, then send the user to login
            const queued = this.refreshSubscribers;
            this.refreshSubscribers = [];
            queued.forEach(({ reject }) => reject(refreshError));

            this.handleAuthError();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        // Handle other errors
        return this.handleError(error);
      }
    );
  }

  private handleError(error: AxiosError): Promise<never> {
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;

      // Surface the server's own message ("Invalid email or password") instead
      // of axios's "Request failed with status code 401". error.response is
      // left untouched for callers that read the full payload.
      const serverMessage = (data as any)?.error?.message || (data as any)?.message;
      if (typeof serverMessage === 'string' && serverMessage) {
        error.message = serverMessage;
      }

      switch (status) {
        case 403:
          console.error('Access forbidden:', data);
          break;
        case 429:
          console.error('Rate limit exceeded');
          break;
        case 500:
          console.error('Server error:', data);
          break;
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network error:', error.message);
    }

    return Promise.reject(error);
  }

  private handleAuthError() {
    // Clear auth state and redirect to login.
    // Already on /login means a credential failure is being shown - leave it be.
    if (window.location.pathname === '/login') return;

    localStorage.clear();
    window.location.href = '/login';
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  // HTTP Methods
  async get<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: any): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }
}

export const api = new ApiClient();
